import { Box, CircularProgress, Stack, Typography } from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import { DateTime } from 'luxon'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import CustomFormControl from '@/components/inputs/CustomFormControl'
import MainContentContainer from '@/components/layouts/MainContentContainer'
import Page from '@/components/layouts/Page'
import { useShellFullWidth } from '@/components/layouts/Shell'
import { BiddingDashboardTabs } from '@/features/bidding/components/BiddingDashboardTabs'
import TreePortfolioSelector from '@/features/bidding/components/TreePortfolioSelector'
import { MARKET_TIMEZONE } from '@/features/bidding/constants'
import { useBidDashboardData } from '@/features/bidding/hooks/useBidDashboardData'
import { usePortfoliosQuery } from '@/features/bidding/hooks/usePortfoliosQuery'

const BiddingDashboard = () => {
  const { i18n, t } = useTranslation()
  const { setIsFullWidth } = useShellFullWidth()
  const [selectedDeliveryDay, setSelectedDeliveryDay] = useState<DateTime>(DateTime.now().setZone(MARKET_TIMEZONE))
  const [selectedPortfolioCode, setSelectedPortfolioCode] = useState<string>('FI-A')

  const { portfolios } = usePortfoliosQuery()

  const selectedPortfolio = useMemo(
    () => portfolios?.find((p) => p.code === selectedPortfolioCode),
    [portfolios, selectedPortfolioCode],
  )

  const countryCode = selectedPortfolio?.countryCode.toLowerCase() as CountryIdentifier | undefined

  const { activationGroups, allocatedVolumes, isLoading } = useBidDashboardData(
    countryCode,
    selectedPortfolioCode,
    selectedDeliveryDay,
  )

  // Enable full-width mode for this page
  useEffect(() => {
    setIsFullWidth(true)
    return () => setIsFullWidth(false)
  }, [setIsFullWidth])

  const centerStyle = { height: '100%', width: '100%', alignItems: 'center', justifyContent: 'center' } as const

  function renderContent() {
    if (!selectedPortfolioCode) {
      return (
        <Stack sx={centerStyle}>
          <Typography color="text.secondary" variant="h6">
            {t('activation_plan.select_portfolio')}
          </Typography>
        </Stack>
      )
    }

    if (isLoading) {
      return (
        <Stack sx={{ ...centerStyle, paddingTop: 3 }}>
          <CircularProgress title="Spinner" />
          <p>Loading all the bids, please wait...</p>
        </Stack>
      )
    }

    return <BiddingDashboardTabs activationGroups={activationGroups} allocatedVolumes={allocatedVolumes} />
  }

  return (
    <Page
      pageHeaderProps={{
        pageTitle: t('component.page_header.export_allocated_volumes'),
        breadcrumbItems: [{ text: t('component.page_header.bidding'), to: '/bidding' }],
        appBarContent: (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TreePortfolioSelector
              portfolios={portfolios}
              value={selectedPortfolioCode}
              onChange={setSelectedPortfolioCode}
            />

            <LocalizationProvider adapterLocale={i18n.language} dateAdapter={AdapterLuxon}>
              <CustomFormControl variant="outlinedWhite">
                <DatePicker
                  enableAccessibleFieldDOMStructure={false}
                  format="DD"
                  label={t('bidding.bidding_cascading.date_picker_label')}
                  sx={{ width: 180 }}
                  value={selectedDeliveryDay}
                  onChange={(date: DateTime | null) => {
                    if (date) setSelectedDeliveryDay(date)
                  }}
                />
              </CustomFormControl>
            </LocalizationProvider>
          </Box>
        ),
      }}
    >
      <MainContentContainer
        sx={{
          p: 0,
          // Calculate height: 100vh minus Shell toolbar (64px), Container margin (16px),
          // PageTitle (~40px), and PageAppBar with margin (~80px)
          height: 'calc(100vh - 216px)',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ display: 'flex', width: '100%', height: '100%', padding: 4 }}>{renderContent()}</Box>
      </MainContentContainer>
    </Page>
  )
}

export default BiddingDashboard
